import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import DatePicker from 'react-datepicker';
import moment from 'moment';

import FormGroup from '../helpers/FormGroup';
import FieldError from '../helpers/FieldError';

/**
 * User datepicker input component.
 *
 * @param {object} props
 * @param {string} props.label Label of text control.
 * @param {string} props.name Name of text control.
 * @param {Function} props.formMode Form mode (view|edit).
 * @param {Immutable.Map} props.formError Error on form.
 * @param {string} props.className Input class name.
 * @returns {React.FunctionComponent}
 */
const UserDateInputComponent = ({ onChange, label, className, formMode, formError, format, name, value, disabled }) => {
  const id = `${label.replace(/\s/g, '_')}_Input`;
  const error = formMode === 'edit' ? formError.get(name, '') : '';
  const inputClassName = `form-control ${disabled ? '' : className}`;
  const formGroupClassName = error ? 'has-error' : '';

  const parsedValue = useMemo(() => {
    if (!value) return null;

    const date = moment.isMoment(value) ? value.clone() : moment(value);

    return date.isValid() ? date : null;
  }, [value]);

  /**
   * Prevent typed text from reaching the form, only valid dates are passed via onChange.
   * @param event Input change event.
   */
  const handleInputChange = useCallback(event => {
    event.stopPropagation();
  }, []);

  return (
    <FormGroup className={formGroupClassName}>
      <label htmlFor={id}>{label}</label>
      <DatePicker
        className={inputClassName}
        dateFormat={format}
        disabled={disabled}
        dropdownMode="select"
        id={id}
        name={name}
        onChange={onChange}
        onChangeRaw={handleInputChange}
        selected={parsedValue}
        showMonthDropdown
        showYearDropdown
      />
      <FieldError message={error} />
    </FormGroup>
  );
};

UserDateInputComponent.propTypes = {
  className: PropTypes.string,
  format: PropTypes.string.isRequired,
  formError: PropTypes.instanceOf(Map).isRequired,
  formMode: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

export const UserDateInput = memo(UserDateInputComponent);
